import {type CreatePaymentMethodGiropayData} from '@stripe/stripe-js';
import {type StripeGateway, type IStripeContext} from '../../../models/stripe';
import {PeachPayCustomer} from '../../../reducers/peachPayCustomerReducer';
import {getLocaleText} from '../../../util/translation';
import giropayBadgeURL from '../../../../img/badge/giropay.svg';
import giropayImageURL from '../../../../img/marks/giropay.svg';
import {Feature} from '../../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../../@type/features';

function initStripeGiropayMethod(): StripeGateway {
	return {
		config: {
			name: Feature.metadata<string>(FeatureFlag.STRIPE_GIROPAY_GATEWAY, 'title') ?? 'giropay',
			gatewayId: 'peachpay_stripe_giropay',
			description: getLocaleText('After selecting <b>Pay</b> you will be redirected to complete your payment.'),
			assets: {
				title: {src: giropayImageURL},
				badge: {src: giropayBadgeURL},
			},
		},
		async tokenize(context) {
			const {stripe} = context;

			const {paymentMethod, error} = await stripe.createPaymentMethod({
				type: 'giropay',
				billing_details: PeachPayCustomer.stripeBillingDetails(),
			} as CreatePaymentMethodGiropayData);

			if (error) {
				const message = error.message ?? 'Failed to tokenize payment method.';

				return [null, {
					paymentStatus: 'creationfailed',
					note: message,
				}];
			}

			if (!paymentMethod?.id) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to tokenize payment method.',
				}];
			}

			return [paymentMethod.id, {}];
		},
		async confirm(context: IStripeContext, clientSecret: string, intermediateURL: string) {
			const {stripe} = context;

			const {error, paymentIntent} = await stripe.confirmGiropayPayment(clientSecret, {return_url: intermediateURL});

			if (error) {
				const message = error.message ?? 'Failed to confirm payment.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent) {
				return ['success', {}];
			}

			return [null, {}];
		},
	};
}

export {initStripeGiropayMethod};
